import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import globals from 'scoreboard/components/globals'
import BaseRouter from 'scoreboard/components/base-router'
import Nav from './initiatives-nav'
import Overview from './overview/initiatives-overview'
import Timeline from './timeline/initiatives-timeline'
import Initiative, {
	getDefaultTypeForTree
} from 'scoreboard/initiatives/initiative'
import appDisplay from 'scoreboard/components/app-display'
import Edit from './edit/initiatives-edit'
import ExportPdf from './export/initiatives-overview-export-pdf'
import EmptyState from 'scoreboard/components/empty-state'
import conductor from 'scoreboard/components/conductor'
import layoutManager from 'scoreboard/components/layout-manager'
import exportState from 'scoreboard/components/export/export-state'
import mobile from 'scoreboard/components/mobile'
import persistedViewState from 'scoreboard/components/persisted-view-state'
import briefingsLayoutManager from 'scoreboard/briefings/briefings-layout-manager'

var InitiativesRouter = BaseRouter.extend({

	/**
	 * Note: From Backbone's online docs:
	 *
	 * Backbone Routers now handle query params in route fragments,
	 * passing them into the handler as the last argument.
	 * Routes specified as strings should no longer include the query string
	 * ('foo?:query' should be 'foo').
	 *
	 * This is leveraged for exports to extract a `state` query param if one exists
	 */

	routes: {
		'initiatives/:id(/)': 'overview',
		'initiatives/:id/note/:noteId(/)': 'overviewNote',
		'initiatives/:id/note(/)': 'overviewNewNote',
		'initiatives/:id/table(/)': 'overviewTable',
		'initiatives/:id/timeline(/)': 'timeline',
		'initiatives/:id/timeline/export(/)': 'timelineExport',
		'initiatives/:id/edit(/)': 'edit',
		'initiatives/:id/new(/)': 'create',
		'organizations/:orgId/initiatives/new(/)': 'createFirst',
		'initiatives/:id/export(/)': 'exportPdf',
		'initiatives/:id/table/export(/)': 'exportPdf'
	},

	Nav: Nav,
	Model: Initiative,
	section: 'initiatives',

	// Routing here in the initiatives section is done a little differently
	// in order to preserve a smooth experience as we navigate from view to
	// view within or on our way out of the section. initRouteWatching() is where
	// we setup a listener for route switching and decide what to do to the layout
	// based on the current and previous history locations.
	initRouteWatching: function() {
		if (this.isRouteWatching) {
			return
		}

		// If, while in the initiatives section, we make the navPane visible,
		// that should cancel out the navPane being hidden outside this section
		layoutManager.on('toggle:isNavCollapsed', (collapsed) => {
			if (!collapsed) {
				this.keepNavPaneCollapsed = false
			}
		})

		this.isRouteWatching = true
		this.keepNavPaneCollapsed = layoutManager.isNavCollapsed()

		var listener
		Backbone.history.on('route', listener = function() {
			var next = Backbone.history.location.pathname
			var fromBriefing, fromBookmark, fromInitiative, fromTimeline,
				toBriefing, toBookmark, toInitiative, toTimeline

			// If there is no previous view, that means we're entering the router
			// from somewhere else in the app
			if (!this.previousPathname) {
				this.previousPathname = next
				return
			}

			fromBriefing = this.previousPathname.indexOf('briefings') >= 0
			toBriefing = next.indexOf('briefings') >= 0
			fromBookmark = this.previousPathname.indexOf('bookmarks') >= 0
			toBookmark = next.indexOf('bookmarks') >= 0
			fromInitiative = this.previousPathname.indexOf('initiatives') >= 0
			toInitiative = next.indexOf('initiatives') >= 0
			fromTimeline = this.previousPathname.indexOf('timeline') >= 0
			toTimeline = next.indexOf('timeline') >= 0

			// Is this route change taking us out of an initiative in a bookmarks or briefing
			// view to another bookmark for briefing. In these cases, we don't want to make any changes
			// to the layout, we can just leave.
			if ((fromBriefing || fromBookmark) && (toBookmark || toBriefing)) {
				return
			}

			// Are we navigating away from an initiative view?
			if (!toInitiative) {
				// Make sure the nav-pane is collapsible
				layoutManager.toggleNavCollapsible(true)

				let isPresentationMode = briefingsLayoutManager.isBriefingPresentationMode()

				// If we're leaving a bookmarked initiative or one that was in a briefing
				// slide and if the navPane wasn't already pre-set upon entering,
				// and if we aren't in the briefings presentation mode
				// (we could be going to another slide) keep the navPane at whatever
				// collapsed state it's in now.
				if ((fromBookmark || fromBriefing)
					&& !this.keepNavPaneCollapsed
					&& !isPresentationMode) {
					layoutManager.toggleNavCollapsed(layoutManager.isNavCollapsed())
					return
				}

				// If the nav pane wasn't collapsed upon entering the initiatives section,
				// make sure it isn't collapsed now when we're leaving. If we're
				// headed to another briefing view (LAYOUT_BRIEFINT_PRESENTATION), don't toggle the nav-pane because
				// the layout should be handled by the briefer
				if (!this.keepNavPaneCollapsed && !isPresentationMode) {
					layoutManager.toggleNavCollapsed(false)
				}

				// Nix the following props since they're used to keep track of where
				// we are while navigating from an initiative view to an initiative view
				this.isRouteWatching = false
				this.previousOrgId = null
				this.previousPathname = null
				this.keepNavPaneCollapsed = null
				// Stop listening so this isn't hit while routes are taking place outside the initiatives section
				Backbone.history.off('route', listener)
				return
			}

			// We were previously within the initiatives timeline tab
			if (fromInitiative && fromTimeline) {
				// If we're going to the overview or edit tabs, make the nav toggler visible again
				// and make the nav-pane collapsed or not based on whether it was previously within this section.
				if (!toTimeline) {
					layoutManager.toggleNavCollapsible(true)
					if (mobile.isMobileOrientation()) {
						// If we're in mobile and we're entering the timeline tab, we should set
						// expandNavUponLeavingMobile to true so that when we leave the mobile orientation,
						// we'll know to expand the nav pane
						this.expandNavUponLeavingMobile = true
					} else {
						// If we're not in the mobile orientation, just expand the navPane as
						// we normally would
						layoutManager.toggleNavCollapsed(this.keepNavPaneCollapsed)
							// #25007 when the transition is done, do a display resize. This
							// is particularly necessary for the initiative chart
							.then(() => {
								// Sometimes, when the network connection lags and we're switching from
								// the timeline tab to the ovierview tab, the timeline tab remains
								// the active displayView at this point. In those cases, we don't
								// want to do any resizing because that'll result in a nav pane collapse.
								// https://github.com/SpiderStrategies/Scoreboard/issues/27327
								let currentDisplay = appDisplay.displayView()
								if (currentDisplay.className.indexOf('initiatives-timeline') >= 0) {
									currentDisplay.remove()
								}

								appDisplay.resize()
							})
					}
				// Else, we must be changing routes within the timeline control, so keep the
				// nav toggler hidden and the nav collapsed
				} else {
					layoutManager.toggleNavCollapsible(true)
					layoutManager.toggleNavCollapsed(true)
				}
			}

			this.previousPathname = next
		}.bind(this))

		$(window).on('resize', () => this.resize())
	},

	overview: function(id, mode, noteId, newNote) {
		this.initRouteWatching()
		// If we're navigating to a new org, while coming from the timeline tab,
		// redirect to the timeline tab instead of navigating to the overview tab (#16964)
		if (this.previousOrgId && this.previousOrgId != globals.orgId &&
			this.previousPathname.indexOf('initiatives') >= 0 && this.previousPathname.indexOf('timeline') >= 0) {
			this.previousOrgId = globals.orgId
			conductor.navigate(`initiatives/${id}/timeline`)
			this.timeline(id)
			return
		}

		this.previousOrgId = globals.orgId
		mode = mode || 'graph'
		var modelPromise = this.load(id, Overview.loadOptions)
		appDisplay.display(modelPromise, model => {
			this._overview = new Overview({ model, mode, noteId, newNote })
			return this._overview
		})
		this.navForModel({ id }, modelPromise).then(navView => {
			this._handleNonTimelineSearch(navView)
		})
		return modelPromise
	},

	// Called right before the view the router is associated with is displayed
	// so that any router-oriented adjustments/checks can be done when entering the section.
	beforeOverviewDisplay: function() {
		if (mobile.isMobileOrientation()) {
			this.keepNavPaneCollapsed = $('.nav-pane').hasClass('is-collapsed')
		} else {
			this.keepNavPaneCollapsed = layoutManager.isNavCollapsed()
		}
	},

	overviewTable: function(id) {
		this.overview(id, 'table')
	},

	overviewNote: function(id, noteId) {
		this.overview(id, 'graph', noteId, false)
		conductor.navigate(`initiatives/${id}`)
	},

	overviewNewNote: function(id) {
		this.overview(id, 'graph', undefined, true)
		conductor.navigate(`initiatives/${id}`)
	},

	timeline: function(initiativeId) {
		this.initRouteWatching()
		var onceOrgIdSet = function() {
			var externalTimeline
			// If we're displaying the timeline view from within the initiatives section
			// (e.g. bookmarks) then set this.previousOrgId
			if (Backbone.history.location.pathname.indexOf('initiatives') >= 0) {
				this.previousOrgId = globals.orgId
			} else {
				externalTimeline = true
			}

			// Check to see if we're coming to this initiatives timeline view from the initiatives overview
			// or edit tabs. If that's the case, we need to save the nav-pane
			// collapsed state in order to keep it consistent as we change tabs within the initiatives section.
			if (!externalTimeline && this.previousPathname
					&& this.previousPathname.indexOf('initiatives') >= 0
					&& this.previousPathname.indexOf('timeline') < 0) {
				if (mobile.isMobileOrientation()) {
					this.keepNavPaneCollapsed = $('.nav-pane').hasClass('is-collapsed')
				} else {
					this.keepNavPaneCollapsed = layoutManager.isNavCollapsed()
				}
			}

			// If we're going to the timeline tab with a nav search term in place,
			// we want to make sure that search is reflected in the timeline tool as well
			var searchTerm = appDisplay.navView() && appDisplay.navView().gardener.finder.text()

			$.ajax({
				method: 'GET',
				url: `api/initiatives/${initiativeId}/calculations`,
				success: data => {
					this.ganttTimeline = new Timeline({
						initiativeId,
						data: data,
						state: persistedViewState(exportState.fromQueryString),
						external: externalTimeline,
						searchTerm
					}).on('remove', () => {
						var navView = appDisplay.navView()
						if (navView && navView.toggleNavTree) {
							navView.toggleNavTree(true)
						}
					})
					appDisplay.display(this.ganttTimeline)
					this.setBrieferView(this.ganttTimeline)
				}
			})

			this.nav({ id: initiativeId, showColors: true }).then(navView => {
				// There should not be anything in the nav tree for the initiatives timeline view
				// so toggle it off here. Only do this if in the initiatives section, not when
				// a timeline shows up elsewhere (i.e. bookmarks/briefings)
				if (conductor.section() == 'initiatives') {
					navView.gardener._onceTreeLoaded(() => {
						_.invoke(navView, 'toggleNavTree', false)

						// If we're in mobile, go ahead and close the mobile menu since there's
						// nothing to see there
						if (mobile.isMobileOrientation()) {
							layoutManager.toggleMobileMenu(true)
						}
					})
				}
			})
		}.bind(this)

		if (globals.orgId) {
			onceOrgIdSet()
		} else {
			this.load(initiativeId, null, function(model) {
				globals.orgId = model.get('organizationId')
				onceOrgIdSet()
			})
		}

		// If we're in mobile and the .nav-pane isn't really (it is hidden because we're in mobile
		// but it doesn't have the .is-collapsed class), set expandNavUponLeavingMobile to true
		// to that we can expand the nav when we leave the mobile orientation
		this.expandNavUponLeavingMobile = mobile.isMobileOrientation() && !$('.nav-pane').hasClass('is-collapsed')
	},

	timelineExport(id) {
		this.load(id, null, model => {
			globals.orgId = model.get('organizationId')
			var initiative = new Initiative({
				organizationId: globals.orgId,
				type: 'initiative',
				id: id
			})

			appDisplay.display($.get(`api/organizations/${globals.orgId}/initiatives/calculations`), (data) => {
				return new Timeline({
					initiativeId: initiative.id,
					data: data,
					state: persistedViewState(exportState.fromQueryString),
					external: true,
					showFooter: true
				})
			})
		})
	},

	edit: function(id) {
		this.initRouteWatching()
		this.previousOrgId = globals.orgId
		const modelPromise = this.load(id, { assignees: true, tags: true })
		appDisplay.display(modelPromise, model => {
			var editable = model.isEditable()
			return editable ? new Edit({ model }) :
				new EmptyState({ body : this._notEditableBody(model) })
		})
		this.navForModel({ id, toggleEdit: true }, modelPromise).then(navView => {
			this._handleNonTimelineSearch(navView)
		})
	},

	// If we're loading the edit or overview tabs, check to see if this.ganttTimeline
	// is defined. If it is, copy it's search to the overview/edit navpane finder/tree
	_handleNonTimelineSearch: function(navView) {
		if (this.ganttTimeline) {
			var searchTerm = this.ganttTimeline.graphView.finder.text()
			navView.gardener.finder.text(searchTerm)
			navView.gardener.tree.search(searchTerm)
			this.ganttTimeline = null
		}
	},

	// orgId is used when creating a top-level initiative by the createFirst
	// function below
	create: function (relativeId, orgId) {
		this.initRouteWatching()

		$.when(this._resolveOrg(relativeId, orgId)).done(() => {
			let organizationId = orgId || globals.orgId

			this.nav({ id: relativeId, orgId: organizationId, toggleEditViewState: true }).then(navView => {
				// #26156 - Need the tree to determine the kind of node to create
				navView.gardener._onceTreeLoaded(() => {
					var initiative = new Initiative({
						organizationId,
						type: getDefaultTypeForTree(navView.gardener.tree, relativeId),
						relativeId
					})

					var edit = new Edit({ model: initiative })
					appDisplay.display(edit)
					this.addTransient(edit, relativeId, initiative)
				})
			})
		})
	},

	createFirst: function (orgId) {
		this.create(null, orgId)
	},

	exportPdf: function(id) {
		var loadOptions = {
			assignees: true,
			relatedItems: true,
			notes: true
		}
		var promise = this.load(id, loadOptions)
		appDisplay.display(promise, function (model) {
			return new ExportPdf({model: model, mode: 'graph'})
		}.bind(this))
		_.result(appDisplay.navView(), 'removeTransient')
		return promise
	},

	resize: function() {
		// If we are to expand the navPane upon leaving mobile, do so
		if (this.expandNavUponLeavingMobile && !mobile.isMobileOrientation()) {
			layoutManager.toggleNavCollapsed(!this.expandNavUponLeavingMobile)
			this.expandNavUponLeavingMobile = false
		}
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.ganttTimeline], 'remove')
	}
})

export default InitiativesRouter

InitiativesRouter.__filename__ = 'initiatives/initiatives-router.js'
